// src/shared/rota-cells.js - the rota CELL RULES, in one place (v136.7).
//
// Moved verbatim out of src/storeRotas/index.js (v136.3), which imports them back, so the admin Store rotas page,
// the admin Store overview (its rota card, built by the Worker) and the store overview email all draw a week the same
// way. They are the staff app's rota cells (src/rota/index.js renderRotaWeek): shift times, D/O, cover in from
// another store, away at another store, TOIL (with its hours when part of a day), Hol, Sick, APL, Not in store.
//
// PURE: no DOM, no globals, no dates from the clock. The one thing the page used to read by bare name, dates.js's
// shiftMins, is copied here as shiftMinutes with the same maths (an overnight finish wraps to the next day), so the
// Worker can run these rules too. Both the page and the Worker import this file; esbuild bundles it into
// dist/storeRotas.js and wrangler into the Worker.

export const DAYS_UAE = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const DAYS_QATAR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// dates.js timeMins + shiftMins, verbatim in behaviour: 'HH:MM' to minutes; a finish before the start is the next day.
function timeMinutes(t) {
  if (!t) return 0;
  const p = String(t).split(':').map(Number);
  return p[0] * 60 + (p[1] || 0);
}
export function shiftMinutes(start, finish) {
  if (!start || !finish) return 0;
  const sm = timeMinutes(start);
  const fm = timeMinutes(finish);
  if (fm < sm) return fm + 1440 - sm;
  return fm - sm;
}

// The store's short name on an "away" chip, the staff app's own rule.
export function shortStore(name) {
  return String(name || '').replace('ELC - ', '').split(' - ').pop().split(' ').slice(0, 2).join(' ');
}
// A shifts row -> the shape the staff app keeps in memory (loadShiftsFromDB).
export function fromRow(row) {
  return {
    type: row.type,
    start: row.start_time || undefined,
    finish: row.end_time || undefined,
    lunch: row.lunch_mins || 0,
    workStore: row.work_store || null,
    leaveReason: row.leave_reason || null,
    toilHours: (row.toil_hours == null ? null : Number(row.toil_hours)),
  };
}
// Paid hours of one leg: the staff app's Rota total (uncapped since v127.34; a missing lunch counts as 60 minutes).
export function legHours(sh) {
  if (!sh || sh.type !== 'work' || !sh.start) return 0;
  return Math.max(0, shiftMinutes(sh.start, sh.finish) / 60 - (sh.lunch || 60) / 60);
}

// One cell. The icon is kept apart from the word so the word alone is translated (src/i18n looks up whole text).
// kind: work | off | toil | holiday | sick | apl | oos | away | empty | none. `store` is the store on screen,
// `isCover` whether this row is someone covering in from another store. text is English as shown; place is a store
// name to show after it (never translated).
export function cellFor(sh, sh2, store, isCover) {
  const shWorkStore = sh ? sh.workStore : null;
  const atThisStore = !shWorkStore || shWorkStore === store;
  // Someone covering in, on a day they are not here: a dash (the staff app's "at home" placeholder).
  if (isCover && (!sh || !atThisStore)) return { kind: 'none', text: '-', title: 'Not at this store this day' };
  // This store's own person working at another store that day.
  if (!isCover && sh && sh.type === 'work' && shWorkStore && shWorkStore !== store) {
    return { kind: 'away', icon: '↗', text: '', place: shortStore(shWorkStore), title: 'Working at another store' };
  }
  if (!sh || sh.type === 'work') {
    if (sh && sh.start) {
      const cell = { kind: 'work', text: sh.start + '–' + sh.finish, hours: legHours(sh) };
      if (sh2 && sh2.type === 'work' && sh2.start) {
        cell.second = sh2.start + '–' + sh2.finish;
        cell.hours += legHours(sh2);
        if (sh2.workStore && sh2.workStore !== store) cell.secondPlace = shortStore(sh2.workStore);
      }
      return cell;
    }
    return { kind: 'empty', text: '-', title: 'Not on the rota' };
  }
  if (sh.type === 'off') return { kind: 'off', text: 'D/O' };
  // v132.94 in the staff app: a part-balance TOIL day shows its hours.
  if (sh.type === 'toil') return { kind: 'toil', text: 'TOIL' + ((sh.toilHours > 0 && sh.toilHours < 8) ? ' ' + sh.toilHours + 'h' : '') };
  if (sh.type === 'holiday') return { kind: 'holiday', icon: '☀️', text: 'Hol' };
  if (sh.type === 'sick') return { kind: 'sick', icon: '🤒', text: 'Sick' };
  if (sh.type === 'apl') {
    const r = sh.leaveReason ? String(sh.leaveReason) : '';
    return { kind: 'apl', text: 'APL', title: r ? r.charAt(0).toUpperCase() + r.slice(1) + ' leave' : 'Authorised paid leave' };
  }
  if (sh.type === 'oos') return { kind: 'oos', icon: '✕', text: 'Not in store' };
  return { kind: 'empty', text: '-' };
}

// The week for one store. rows = the /api/shifts answer; staff = staffData (id, name, store, active).
// -> { store, weekStr, days, people: [{ id, name, home, cover, cells: { Mon: cell }, hours }], heads: { Mon: n }, empty }
export function buildModel({ store, rows, staff, weekStr, days }) {
  const list = Array.isArray(staff) ? staff : [];
  const legs = {};   // staffId_day -> { 1: sh, 2: sh }
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row || row.staff_id == null) return;
    if (weekStr && row.week_start && row.week_start !== weekStr) return;   // another week's row: never shown here
    const k = row.staff_id + '_' + row.day;
    const seq = (row.seq || 1) >= 2 ? 2 : 1;
    (legs[k] = legs[k] || {})[seq] = fromRow(row);
  });
  const home = list.filter((s) => s && s.store === store && s.active !== false)
    .slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  const homeIds = {};
  home.forEach((s) => { homeIds[s.id] = true; });
  // People covering in: a shift AT this store this week by someone whose home is elsewhere.
  const coverIds = [];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row || row.staff_id == null || homeIds[row.staff_id]) return;
    if (weekStr && row.week_start && row.week_start !== weekStr) return;
    if (row.work_store !== store) return;
    if (coverIds.indexOf(row.staff_id) === -1) coverIds.push(row.staff_id);
  });
  const cover = coverIds.map((id) => {
    const s = list.find((x) => x && String(x.id) === String(id));
    return { id, name: s ? s.name : '', store: s ? s.store : '' };
  }).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  const heads = {};
  days.forEach((d) => { heads[d] = 0; });
  const people = [];
  const add = (s, isCover) => {
    const cells = {};
    let hours = 0;
    days.forEach((d) => {
      const l = legs[s.id + '_' + d] || {};
      const c = cellFor(l[1], l[2], store, isCover);
      cells[d] = c;
      if (c.kind === 'work') { heads[d]++; hours += c.hours || 0; }
    });
    people.push({ id: s.id, name: s.name || '', home: s.store || '', cover: isCover, cells, hours });
  };
  home.forEach((s) => add(s, false));
  cover.forEach((s) => add(s, true));
  const anyShift = people.some((p) => days.some((d) => p.cells[d].kind !== 'empty' && p.cells[d].kind !== 'none'));
  return { store, weekStr, days, people, heads, empty: !anyShift };
}

// ── For the Store overview (v136.7): one cell as plain text, and the overview's kind names ──────────────────────
// The overview payload names kinds work | off | hol | sick | toil | apl | away | oos | none (its API contract);
// a holiday is 'hol' and a day with nothing on the rota is 'none', the same thing the page above calls 'empty'.
export function overviewKind(kind) {
  if (kind === 'holiday') return 'hol';
  if (kind === 'empty') return 'none';
  return kind || 'none';
}
// The words a cell shows, as one string (an email has no chips): "09:00–18:00", "09:00–13:00 + 18:00–22:00",
// "↗ Test Centre", "☀️ Hol", "TOIL 4h", "-".
export function cellText(c) {
  if (!c) return '-';
  if (c.kind === 'away') return (c.icon ? c.icon + ' ' : '') + (c.place || '');
  let t = (c.icon ? c.icon + ' ' : '') + (c.text || '');
  if (c.place) t += ' ' + c.place;
  if (c.second) t += ' + ' + c.second + (c.secondPlace ? ' ↗ ' + c.secondPlace : '');
  return t;
}
